import { el } from "./dom.js";
import { fmtDur } from "./format.js";
import { api } from "../net/api.js";

interface ConnRow {
  username: string; guest: boolean; ip: string | null;
  map_id: string | null; mode: string | null;
  connected_at: string; disconnected_at: string | null;
  duration_s: number; live: boolean;
}

// IP → "City, Country" via ipwho.is (free, CORS), cached forever in
// localStorage so each address is looked up exactly once
const geoCache: Record<string, string> = (() => {
  try { return JSON.parse(localStorage.getItem("np.geoip") ?? "{}") as Record<string, string>; } catch { return {}; }
})();
const inflight = new Map<string, Promise<string>>();
function locate(ip: string | null): Promise<string> {
  if (!ip) return Promise.resolve("?");
  if (/^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|127\.|::1|fe80|fc|fd)/.test(ip)) return Promise.resolve("LAN");
  if (geoCache[ip]) return Promise.resolve(geoCache[ip]);
  const running = inflight.get(ip);
  if (running) return running;
  const job = fetch(`https://ipwho.is/${encodeURIComponent(ip)}`)
    .then((r) => r.json())
    .then((j: { success?: boolean; city?: string; country?: string }) => {
      const loc = j && j.success !== false ? [j.city, j.country].filter(Boolean).join(", ") || "?" : "?";
      if (loc !== "?") {
        geoCache[ip] = loc;
        try { localStorage.setItem("np.geoip", JSON.stringify(geoCache)); } catch { /* full — fine */ }
      }
      return loc;
    })
    .catch(() => "?")
    .finally(() => inflight.delete(ip));
  inflight.set(ip, job);
  return job;
}


/** Owner-only: the connection log — who joined, when, from where, how long. */
export function adminScreen(onBack: () => void): HTMLElement {
  const screen = el("div", { class: "screen overlay" });
  const panel = el("div", { class: "panel", style: "min-width:min(880px,95vw);max-width:95vw" });
  const list = el("div", { style: "max-height:62vh;overflow:auto;margin-top:0.6rem;font-size:0.85em" });
  const status = el("div", { class: "hint", text: "loading…" });

  const load = (): void => {
    status.textContent = "loading…";
    void api.adminConnections().then((r) => {
      const rows = r.connections as ConnRow[];
      status.textContent = `${rows.length} connections (latest first) · ${rows.filter((x) => x.live).length} live`;
      const header = el("div", { class: "row", style: "display:grid;grid-template-columns:1.3fr 0.55fr 1.05fr 1.05fr 1fr 1.3fr 0.85fr;gap:0.5rem;opacity:0.6;padding:0.2rem 0.4rem" },
        el("span", { text: "WHO" }), el("span", { text: "TYPE" }), el("span", { text: "FROM (IP)" }),
        el("span", { text: "LOCATION" }), el("span", { text: "WHERE" }), el("span", { text: "WHEN" }), el("span", { text: "HOW LONG" }));
      list.replaceChildren(header, ...rows.map((c) => {
        const when = new Date(c.connected_at);
        const locEl = el("span", { text: "…", style: "opacity:0.85" });
        void locate(c.ip).then((loc) => { locEl.textContent = loc; });
        return el("div", { class: "row", style: `display:grid;grid-template-columns:1.3fr 0.55fr 1.05fr 1.05fr 1fr 1.3fr 0.85fr;gap:0.5rem;padding:0.25rem 0.4rem;border-top:1px solid #222a38;${c.live ? "color:#9dff00" : ""}` },
          el("span", { text: c.username }),
          el("span", { text: c.guest ? "guest" : "account" }),
          el("span", { text: c.ip || "?" }),
          locEl,
          el("span", { text: `${c.map_id ?? "?"} · ${c.mode ?? "?"}` }),
          el("span", { text: `${when.toLocaleDateString()} ${when.toLocaleTimeString()}` }),
          el("span", { text: c.live ? `● LIVE ${fmtDur(c.duration_s)}` : fmtDur(c.duration_s) }),
        );
      }));
    }).catch((err) => { status.textContent = (err as Error).message || "failed to load"; });
  };
  load();

  const refresh = el("button", { class: "ghost", text: "↻ Refresh" });
  refresh.onclick = load;
  const back = el("button", { class: "ghost wide", text: "Back" });
  back.onclick = onBack;
  panel.append(
    el("div", { class: "brand small", text: "NEON PURGE" }),
    el("h2", { text: "👑 CONNEXIONS" }),
    el("div", { class: "row", style: "display:flex;gap:0.6rem;align-items:center" }, status, refresh),
    list,
    back,
  );
  screen.append(panel);
  return screen;
}
